import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ChartDraft, ChartDraftEdit, ChartReturnTarget, UpsertChartDraftRequest } from 'src/app/models/chart-draft.model';
import { AreYouSureModalComponent } from 'src/app/shared/are-you-sure-modal/are-you-sure-modal.component';
import { NewAuthService } from '../auth/new-auth-service.service';
import { PatientService } from '../patient.service';
import { PatientTimerService } from '../patient-timer.service';
import { ChartDraftCanaryService } from './chart-draft-canary.service';
import { ChartDraftService } from './chart-draft.service';
import { ChartEditRegistryService } from './chart-edit-registry.service';

/**
 * Orchestrates leaving a patient chart while preserving in-progress work.
 *
 * Editable surfaces register an edit provider while they are active; when the nurse jumps to
 * another chart (e.g. to take an inbound call) this service snapshots the current edit plus the
 * elapsed timer into the temporary draft, then navigates. The snapshot is read synchronously —
 * before navigation tears the source chart down — and persisted in the background so the jump
 * is never blocked on the network.
 */
@Injectable({
  providedIn: 'root',
})
export class ChartDraftCoordinatorService {
  private pendingCallJump = false;
  /** Patients whose entry draft was already resolved this session (suppresses re-prompting). */
  private homeHandled = new Set<string>();
  /** Patients whose care-plan draft was already restored this session (prevents duplicates). */
  private carePlanRestored = new Set<string>();
  /** Patients whose clinical-note draft was already restored this session (prevents duplicates). */
  private noteRestored = new Set<string>();
  /** The chart to return to after a call jump (drives the "return to previous patient" UI). */
  private returnTargetSubject = new BehaviorSubject<ChartReturnTarget | null>(null);
  readonly returnTarget$ = this.returnTargetSubject.asObservable();

  constructor(
    private chartDraftService: ChartDraftService,
    private patientTimer: PatientTimerService,
    private patientService: PatientService,
    private router: Router,
    private dialog: MatDialog,
    private editRegistry: ChartEditRegistryService,
    private canary: ChartDraftCanaryService,
    private auth: NewAuthService
  ) {
    // Session-scoped state lives on this root singleton. On a shared clinical workstation a
    // logout/login in the same tab (no page reload) would otherwise let the next nurse inherit the
    // previous nurse's return banner and suppressed recovery prompts — reset it on logout.
    this.auth.isLoggedObservable$.subscribe((isLoggedIn) => {
      if (!isLoggedIn) {
        this.resetSessionState();
      }
    });
  }

  /** Whether the feature is enabled for the current user (canary allowlist). For gating UI entry points. */
  get canaryEnabled$(): Observable<boolean> {
    return this.canary.isEnabled$;
  }

  /** The same, right now — for a click handler deciding whether `jumpToPatient` will act. */
  get isCanaryEnabled(): boolean {
    return this.canary.isEnabled;
  }

  private resetSessionState(): void {
    this.homeHandled.clear();
    this.carePlanRestored.clear();
    this.noteRestored.clear();
    this.pendingCallJump = false;
    this.returnTargetSubject.next(null);
  }

  /**
   * Returns true (once) when the in-progress navigation was triggered by a call jump, letting
   * the deactivate guard skip its leave prompts. Self-resets after being read.
   */
  consumeCallJumpFlag(): boolean {
    const wasJump = this.pendingCallJump;
    this.pendingCallJump = false;
    return wasJump;
  }

  /**
   * On entering a chart, checks for a preserved draft and, if found, prompts the nurse to recover
   * or discard it. Recovering keeps the draft (so deferred surfaces like care plans can still load
   * it); the draft is removed later when the patient's time is saved or discarded, or now on discard.
   * Call after the patient and timer have been initialized for the chart.
   */
  checkForDraftOnEntry(patientId: string): void {
    if (!this.canary.isEnabled || !patientId) {
      return;
    }
    // Arrived at the chart we offered to return to — clear the "return" affordance.
    if (this.returnTargetSubject.value?.patientId === patientId) {
      this.returnTargetSubject.next(null);
    }
    if (this.homeHandled.has(patientId)) {
      return;
    }
    this.chartDraftService
      .getDraft(patientId)
      .pipe(take(1))
      .subscribe({
        next: (draft) => {
          if (draft) {
            this.promptRestoreOrDiscard(patientId, draft);
          }
        },
        error: (err) => console.error('[ChartDraft] Failed to load draft on chart entry', err),
      });
  }

  /**
   * For the Care Plans tab: returns the persisted care-plan draft to restore, without prompting,
   * deleting, or marking. Returns null once it has already been restored this session (see
   * {@link markCarePlanRestored}). The draft is removed later when the time is saved or discarded.
   */
  getCarePlanDraftToRestore(patientId: string): Observable<ChartDraftEdit | null> {
    if (!this.canary.isEnabled || !patientId || this.carePlanRestored.has(patientId)) {
      return of(null);
    }
    return this.chartDraftService
      .getDraft(patientId)
      .pipe(
        take(1),
        map((draft) => (draft?.edit?.type === 'care-plan' ? draft.edit : null))
      );
  }

  /** Marks a patient's care-plan draft as restored this session (after it was actually added). */
  markCarePlanRestored(patientId: string): void {
    if (patientId) {
      this.carePlanRestored.add(patientId);
    }
  }

  /**
   * For the clinical-note panel: returns the persisted clinical-note draft to restore, without
   * prompting or deleting. Like the care-plan path, this is pulled by the note panel when it mounts
   * (the panel is hidden on chart entry, so an in-place restore at entry time would no-op and lose
   * the recovered work). Returns null once already restored this session (see {@link markNoteRestored}).
   */
  getNoteDraftToRestore(patientId: string): Observable<ChartDraftEdit | null> {
    if (!this.canary.isEnabled || !patientId || this.noteRestored.has(patientId)) {
      return of(null);
    }
    return this.chartDraftService
      .getDraft(patientId)
      .pipe(
        take(1),
        map((draft) => (draft?.edit?.type === 'clinical-note' ? draft.edit : null))
      );
  }

  /** Marks a patient's clinical-note draft as restored this session (after it was actually applied). */
  markNoteRestored(patientId: string): void {
    if (patientId) {
      this.noteRestored.add(patientId);
    }
  }

  private promptRestoreOrDiscard(patientId: string, draft: ChartDraft): void {
    const modal = this.dialog.open(AreYouSureModalComponent, {
      disableClose: true,
      data: {
        title: 'Unsaved work from a previous session',
        body: this.buildDraftSummary(draft),
        confirmButton: 'Recover',
        cancelButton: 'Discard',
      },
    });

    modal
      .afterClosed()
      .pipe(take(1))
      .subscribe((result) => {
        this.homeHandled.add(patientId);
        if (result?.confirmed) {
          // Recover: seed time + restore mounted edits. Keep the draft so deferred surfaces (care
          // plans) can still load it; it is removed when the patient's time is saved or discarded.
          this.restoreDraft(draft);
        } else {
          // Discard: drop the in-progress edit and remove the draft now.
          this.discardDraftEdit(draft);
          this.chartDraftService.deleteDraft(patientId).subscribe({
            error: (err) => console.error('[ChartDraft] Failed to delete draft after discard', err),
          });
        }
      });
  }

  private restoreDraft(draft: ChartDraft): void {
    // Time belongs to the chart — recover it now, regardless of the edit's surface.
    if (draft.accumulatedSeconds > 0) {
      this.patientTimer.seedTimer(draft.accumulatedSeconds);
    }
    // The edit restores on its own surface once that surface mounts (deferred pulls:
    // getCarePlanDraftToRestore for care plans, getNoteDraftToRestore for clinical notes). We do NOT
    // restore here via findProvider: on chart entry the care-plan tab and the note panel are usually
    // not rendered yet, so the provider isn't registered and restore() would silently no-op — losing
    // the recovered work even though the nurse chose "Recover". The persisted draft is kept until the
    // time is saved or discarded, so each surface can pull it when it mounts.
  }

  private discardDraftEdit(draft: ChartDraft): void {
    if (draft.edit) {
      this.editRegistry.findProvider(draft.edit.type)?.discard?.();
    }
  }

  private buildDraftSummary(draft: ChartDraft): string {
    const elapsed = this.formatSeconds(draft.accumulatedSeconds);
    const editPart = draft.edit?.summary ? ` and an unsaved edit (${draft.edit.summary})` : '';
    return `You left ${elapsed} of tracked time${editPart}. Recover it, or discard?`;
  }

  private formatSeconds(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds || 0));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  /**
   * Saves the current chart's in-progress work to the temporary draft, then navigates to the
   * target patient's chart.
   */
  jumpToPatient(targetPatientId: string): void {
    if (!this.canary.isEnabled) {
      return;
    }
    const source = this.patientService.currentPatientService;
    const sourceId = source?.user_id;

    if (sourceId && sourceId !== targetPatientId) {
      this.saveSourceDraft(sourceId, source);
      const name = `${source?.firstName ?? ''} ${source?.lastName ?? ''}`.trim();
      this.returnTargetSubject.next({ patientId: sourceId, patientName: name || 'previous patient' });
      // Only bypass the leave guard when actually leaving a different chart. A same-patient jump
      // never triggers canDeactivate, so setting the flag there would leak and skip a later prompt.
      this.pendingCallJump = true;
    }

    this.router.navigate(['clinical/patient-detail', targetPatientId]);
  }

  /** Navigates back to the chart preserved by the last call jump, and clears the affordance. */
  returnToDraftPatient(): void {
    const target = this.returnTargetSubject.value;
    if (!this.canary.isEnabled || !target) {
      return;
    }
    this.returnTargetSubject.next(null);
    this.router.navigate(['clinical/patient-detail', target.patientId]);
  }

  /** Dismisses the "return to previous patient" affordance without navigating. */
  dismissReturnTarget(): void {
    this.returnTargetSubject.next(null);
  }

  private saveSourceDraft(patientId: string, patient: { firstName?: string; lastName?: string }): void {
    const edit = this.editRegistry.getActiveEdit();
    const accumulatedSeconds = this.patientTimer.getElapsedSeconds();

    // Nothing worth preserving — skip the write.
    if (!edit && accumulatedSeconds <= 0) {
      return;
    }

    const patientName = `${patient?.firstName ?? ''} ${patient?.lastName ?? ''}`.trim();
    const dto: UpsertChartDraftRequest = {
      patientName: patientName || undefined,
      accumulatedSeconds,
      edit: edit ?? undefined,
    };

    // A fresh draft supersedes any prior session state for this patient.
    this.homeHandled.delete(patientId);
    this.carePlanRestored.delete(patientId);
    this.noteRestored.delete(patientId);

    this.chartDraftService.upsertDraft(patientId, dto).subscribe({
      error: (err) => console.error('[ChartDraft] Failed to save draft on chart jump', err),
    });
  }
}
